import {useState, useEffect, useMemo, useCallback} from 'react';
import dayjs from 'dayjs';
import {getAllDemandes, updateDemandeStatus} from '../../../services/DemandeService';
import {getCollaborateurById} from '../../../services/CollaborateurService';

// "Congé" and "conge" must match: compare without accents or case.
export const normalize = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const toIsoDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '');

export default function useDemandes(typeFilter) {
    const [demandes, setDemandes] = useState([]);
    const [names, setNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const res = await getAllDemandes();
                if (!active) return;
                setDemandes((res.data || []).map(d => ({
                    id: d.id,
                    type: d.sousType || d.type || '',
                    status: d.statut || 'en attente',
                    demandeDate: toIsoDate(d.dateDemande),
                    startDate: toIsoDate(d.dateDebut),
                    endDate: toIsoDate(d.dateFin),
                    applicant: d.collaborateur,
                })));
            } catch {
                if (active) setError('Erreur lors du chargement des demandes.');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    // Load each applicant's name once (the list only has the collaborator id).
    useEffect(() => {
        const ids = [...new Set(demandes.map(d => d.applicant).filter(Boolean))].filter(id => !(id in names));
        if (!ids.length) return;
        let active = true;
        Promise.all(ids.map(id => getCollaborateurById(id)
            .then(resp => [id, resp.data ? `${resp.data.nom} ${resp.data.prenom}` : 'Inconnu'])
            .catch(() => [id, 'Inconnu'])))
            .then(entries => active && setNames(prev => ({...prev, ...Object.fromEntries(entries)})));
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demandes]);

    const rows = useMemo(() => demandes
        .filter(d => normalize(d.type).includes(normalize(typeFilter)))
        .map(d => ({...d, displayApplicant: names[d.applicant] ?? ''})), [demandes, names, typeFilter]);

    const counts = useMemo(() => {
        const c = {all: rows.length, 'en attente': 0, 'livrée': 0, 'refusée': 0};
        rows.forEach(r => { const k = normalize(r.status); Object.keys(c).forEach(key => { if (normalize(key) === k) c[key] += 1; }); });
        return c;
    }, [rows]);

    const filtered = useMemo(() => {
        const q = normalize(search.trim());
        return rows
            .filter(r => statusFilter === 'all' || normalize(r.status) === normalize(statusFilter))
            .filter(r => !q || [r.displayApplicant, r.type, r.status, r.demandeDate, r.startDate, r.endDate].some(v => normalize(v).includes(q)));
    }, [rows, search, statusFilter]);

    // Optimistic status change, rolled back if the server refuses it.
    const changeStatus = useCallback(async (id, status, actorId) => {
        let previous;
        setDemandes(ds => ds.map(d => {
            if (d.id !== id) return d;
            previous = d.status;
            return {...d, status};
        }));
        try {
            await updateDemandeStatus(id, status, actorId);
        } catch (e) {
            setDemandes(ds => ds.map(d => (d.id === id ? {...d, status: previous} : d)));
            throw e;
        }
    }, []);

    return {
        loading, error, rows, filtered, counts,
        search, setSearch, statusFilter, setStatusFilter, changeStatus,
    };
}
